import { Icon, Typography, Button, Card, Tooltip, Progress } from '@equinor/eds-core-react'
import styled from 'styled-components'
import { onboardedEditedAtom, selectedSensorCardAtom, selectedSensorToEditAtom } from '@/core/store/globalStore'
import { useAtom } from 'jotai'
import { Sensor } from '../../models'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetMeasurements } from '@/core/hooks-and-services/measurementsService'
import { InstalledSensorStatus, OmniaTimeSeriesDto, ThumbnailDto } from '@/core/models/types'
import { STATUS_COLORS } from '@/core/models/sensorStatusColors'
import { deleteSensor, updateUserSettings } from '@/api/apiService'
import { isNonProduction } from '@/config/environmentHelpers'
import { isCurrentUserAdmin } from '@/api/AuthConfig'
import { LoadingIIoT } from '@/components/loading/Loading'
import {
    assignment_important,
    check_circle_outlined,
    close_circle_outlined,
    sync_problem,
    more_vertical,
    info_circle,
    help_outline,
    warning_outlined,
} from '@equinor/eds-icons'
import OptionsMenu from './OptionsMenu'
import { useGetUserSettings } from '@/core/hooks-and-services/userSettingsService'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/core/models/enum'
import EditOnboarded from '../EditOnboarded'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { Pagination } from '@equinor/eds-core-react'
import ImageDisplayer from '../EditOnboarded/ImageDisplayer'
import PopoverDialog from '@/components/popover/PopoverDialog'
import { useSensorTagDetails } from '../../hooks/useSensorTagDetails'
import { useSensorDownlinkCapability } from '../../hooks/useSensorDownlinkCapability'
import { useSensorConfigurations } from '../../hooks/useSensorConfigurations'
import { useSensorImages } from '../../hooks/useSensorImages'
import TrendingConsentDialog from './TrendingConsentDialog'
import DownlinkConfiguration from '../downlink/DownlinkConfiguration'
import { formatMeasurementValueWithMaxTwoDecimals, formatTimestampForDisplay } from '@/utils'
import { BatteryLevelIcon, SignalQualityIcon } from '@/components/sensorHealthIcons/SensorHealthIcons'
import { formatSignalQualityDisplay, formatBatteryDisplay } from '@/core/models/sensorHealth'

interface ISensorDetails {
    sensor: Sensor | null
    isMobile: boolean
    editOnboarded: boolean
}

const MeasurementsList: React.FC<{
    measurementData: OmniaTimeSeriesDto[]
    showTrendLinks: boolean
    isTrendingDisabled: boolean
    onTrendingClick: (measurementName?: string | null) => void
}> = ({ measurementData, showTrendLinks, isTrendingDisabled, onTrendingClick }) => {
    const defaultData = '-'

    const isEmpty = (value: unknown): boolean => {
        return value === null || value === undefined || value === ' ' || value === ''
    }

    const capitalizeFirstLetter = (value: string): string => {
        return value.charAt(0).toUpperCase() + value.slice(1)
    }

    const formatHeader = (value: string): string => {
        const lastSegment = value.split('.').pop() ?? value
        return lastSegment
            .split('_')
            .filter((word) => word.length > 0)
            .map((word) =>
                word
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .split(' ')
                    .map(capitalizeFirstLetter)
                    .join(' ')
            )
            .join(' ')
    }

    const displayOrder: Array<keyof OmniaTimeSeriesDto> = ['description']

    const formatRange = (rangeLow: unknown, rangeHigh: unknown, unit?: string | null): string => {
        const low = isEmpty(rangeLow) ? defaultData : rangeLow
        const high = isEmpty(rangeHigh) ? defaultData : rangeHigh

        if (low === defaultData && high === defaultData) return defaultData

        return `${low} → ${high}${unit ? ` [${unit}]` : ''}`
    }

    const buildTrendPagePath = (data: OmniaTimeSeriesDto, title: string): string | null => {
        if (!data.timeseriesTableDbKey) return null

        const unit = data.unit ?? 'dB'
        return `/trends/${encodeURIComponent(data.timeseriesTableDbKey)}/false/${encodeURIComponent(unit)}/70/false/${encodeURIComponent(title)}`
    }

    return (
        <>
            {Object.entries(measurementData).map(([measurementDataKey, measurementDataValues], index) => {
                const data = measurementDataValues as OmniaTimeSeriesDto
                const latestValue = data.latestValue
                const unit = data.unit
                const timestamp = data.latestTime
                const title = formatHeader(data.name ?? measurementDataKey)
                const trendPagePath = showTrendLinks ? buildTrendPagePath(data, title) : null

                return (
                    <MeasurementItemContainer key={measurementDataKey}>
                        <MeasurementHeaderRow>
                            <MeasurementName>{title}</MeasurementName>
                            <MeasurementLatestValue>
                                {latestValue !== undefined && latestValue !== null
                                    ? `${formatMeasurementValueWithMaxTwoDecimals(latestValue)}${unit ? ` ${unit}` : ''}`
                                    : defaultData}
                            </MeasurementLatestValue>
                        </MeasurementHeaderRow>
                        <MeasurementPropertyRow $marginTop="4px">
                            <MeasurementPropertyLabel>Last updated:</MeasurementPropertyLabel>
                            <MeasurementPropertyValue>{formatTimestampForDisplay(timestamp)}</MeasurementPropertyValue>
                        </MeasurementPropertyRow>
                        {(() => {
                            return displayOrder.map((propKey) => {
                                const dataValue = data[propKey]
                                if (dataValue === undefined) return null

                                return (
                                    <MeasurementPropertyRow key={propKey} $marginTop="4px">
                                        <MeasurementPropertyLabel>
                                            {capitalizeFirstLetter(propKey)}:
                                        </MeasurementPropertyLabel>
                                        <MeasurementPropertyValue>
                                            {isEmpty(dataValue) ? defaultData : dataValue}
                                        </MeasurementPropertyValue>
                                    </MeasurementPropertyRow>
                                )
                            })
                        })()}
                        <MeasurementPropertyRow $marginTop="4px">
                            <MeasurementPropertyLabel>Range:</MeasurementPropertyLabel>
                            <MeasurementPropertyValue>
                                {formatRange(data.rangeLow, data.rangeHigh, unit)}
                            </MeasurementPropertyValue>
                        </MeasurementPropertyRow>
                        <ButtonGroup $marginTop="4px" $marginBottom="0px" $justifyContent="flex-end" $gap="24px">
                            <Tooltip
                                title={isTrendingDisabled ? 'Trending only works in production environment' : ''}
                                placement="top"
                            >
                                <Button
                                    variant="ghost"
                                    aria-disabled={isTrendingDisabled}
                                    onClick={isTrendingDisabled ? undefined : () => onTrendingClick(data.name)}
                                >
                                    Trending
                                    <Icon name="external_link" />
                                </Button>
                            </Tooltip>
                            {trendPagePath && (
                                <Button as="a" href={trendPagePath} target="_blank" variant="ghost">
                                    Trend tool
                                    <Icon name="external_link" />
                                </Button>
                            )}
                        </ButtonGroup>
                        {index < Object.entries(measurementData).length - 1 && <MeasurementDivider />}
                    </MeasurementItemContainer>
                )
            })}
        </>
    )
}

const SensorDetails: React.FC<ISensorDetails> = ({ sensor, isMobile, editOnboarded }) => {
    const isDev = isNonProduction()

    const [isExpanded, setIsExpanded] = useState(false)
    const [isConfigurationsExpanded, setIsConfigurationsExpanded] = useState(false)
    const [isConfigurationHelpOpen, setConfigurationHelpOpen] = useState(false)
    const configurationHelpRef = useRef<HTMLDivElement>(null)
    const [isAdmin, setIsAdmin] = useState(false)
    const [, setSelectedSensorToEdit] = useAtom(selectedSensorToEditAtom)

    const [, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)

    const navigate = useNavigate()
    const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(sensor)

    const buildTrendingUrl = (measurementName?: string | null): string => {
        const lastPart = measurementName?.split('.').pop()
        const omniaTag = lastPart ? `${selectedSensor?.tag}.${lastPart}` : selectedSensor?.tag
        return `https://apps.powerapps.com/play/e/39d04b94-76f7-48f9-8ab3-3ab354a81af4/a/7c28eaaf-c500-4850-b5f0-687bf9b1e9e7?tenantId=3aa4a235-b6e2-48d5-9195-7fcf05b459b0&source=portal&hidenavbar=true&omnia=true&omniaplant=${selectedSensor?.plant}&omniatag=${omniaTag}&omniasource=IIOTSENSORTOOLS&omniatimespand=1%20day&restartApp=true`
    }

    const {
        data: measurements,
        isLoading: isLoadingMeasurements,
        isError: hasMeasurementsError,
        error: measurementsError,
    } = useGetMeasurements(selectedSensor?.plant || '', selectedSensor?.tag || '', isExpanded)

    const measurementData = measurements ?? ({} as OmniaTimeSeriesDto[])
    const hasMeasurements = Object.keys(measurementData).length > 0
    const measurementsErrorText =
        measurementsError?.response?.status === 403
            ? 'You do not have access to measurements for this sensor.'
            : 'Could not load measurements. Please try again later.'

    const optionsRef = useRef<HTMLDivElement>(null)

    const [isOptionsMenuOpen, setOptionsMenuOpen] = useState(false)

    const [isOnboardedEditMode, setOnboardedEditMode] = useState(editOnboarded)
    const [isEditSensorOpenedFromOptionsMenu, setEditSensorOpenedFromOptionsMenu] = useState(false)

    const queryClient = useQueryClient()
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
    const [onboardedEdited, setOnboardedEdited] = useAtom(onboardedEditedAtom)
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [itemsPerPage] = useState(3)
    const [imageOpen, setImageOpen] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
    const closeFromMobileBackRef = useRef(false)

    const [showTrendingConsent, setShowTrendingConsent] = useState(false)
    const [isSavingTrendingConsent, setIsSavingTrendingConsent] = useState(false)
    const [pendingTrendingUrl, setPendingTrendingUrl] = useState(() => buildTrendingUrl())
    const [isDownlinkConfigMode, setDownlinkConfigMode] = useState(false)

    const { data, isLoading, error, refetch } = useSensorTagDetails(selectedSensor)

    const sensorAdditionalData = data ?? []
    const tagDetails = sensorAdditionalData[0] ?? null

    const { data: downlinkCapability } = useSensorDownlinkCapability(selectedSensor)
    const canConfigureDownlink = (downlinkCapability?.supportedTypes?.length ?? 0) > 0

    const { data: configurations } = useSensorConfigurations(selectedSensor)

    const { data: sensorImages } = useSensorImages(selectedSensor)

    useEffect(() => {
        setSelectedSensor(sensor)
        setOnboardedEdited(false)
    }, [sensor])

    useEffect(() => {
        setOnboardedEditMode(editOnboarded)
    }, [editOnboarded])

    useEffect(() => {
        let isCancelled = false

        const checkAdminRole = async () => {
            try {
                const hasAdminRole = await isCurrentUserAdmin()

                if (!isCancelled) {
                    setIsAdmin(hasAdminRole)
                }
            } catch {
                if (!isCancelled) {
                    setIsAdmin(false)
                }
            }
        }

        checkAdminRole()

        return () => {
            isCancelled = true
        }
    }, [])

    const showOptionsMenu = () => {
        setOptionsMenuOpen(true)
    }

    const hideOptionsMenu = () => {
        setOptionsMenuOpen(false)
    }

    const onEditSensor = useCallback((sensor: Sensor) => {
        const isOnboarded =
            sensor.status === InstalledSensorStatus.Onboarded ||
            sensor.status === InstalledSensorStatus.Active ||
            sensor.status === InstalledSensorStatus.Offline ||
            sensor.status === InstalledSensorStatus.Prepared

        if (isOnboarded) {
            setOnboardedEditMode(true)
            setEditSensorOpenedFromOptionsMenu(true)
        } else {
            setSelectedSensorToEdit(sensor)
            handleSideBarCloseButton()
            hideOptionsMenu()
        }
    }, [])

    const onConfigureSensor = useCallback(() => {
        setDownlinkConfigMode(true)
        hideOptionsMenu()
    }, [])

    const files = useMemo(() => {
        return tagDetails?.files || []
    }, [sensorAdditionalData])

    const paginatedData = useMemo(() => {
        if (!files) return null
        const startIndex = (currentPageIndex - 1) * itemsPerPage
        return files.slice(startIndex, startIndex + itemsPerPage)
    }, [files, currentPageIndex, itemsPerPage])

    const { data: userSettings } = useGetUserSettings()

    const { data: devices } = useGetDevices()

    const filteredDevices = devices.filter((device) => device.portfolio)
    const sensorDevice = filteredDevices.find((d) => d.key === (selectedSensor?.sensorModel || null))

    const onDeleteSensor = async (sensor: Sensor) => {
        try {
            await deleteSensor(sensor, userSettings?.userId ?? '')
            addSnackBarMessage(`Successfully deleted ${sensor.tag || sensor.vendorSerialNumber}.`)
            await delay(1000)
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.OnboardedSensors],
            })
        } catch (error) {
            console.error('Error deleting sensor:', error)
            addSnackBarMessage(`Failed to delete sensor`)
        } finally {
            handleSideBarCloseButton()
            hideOptionsMenu()
        }
    }

    const renderOptionsMenu = useCallback(
        () => (
            <OptionsMenu
                onClose={hideOptionsMenu}
                sensor={selectedSensor}
                onEditSensor={onEditSensor}
                onDeleteSensor={onDeleteSensor}
                onConfigureSensor={onConfigureSensor}
                hasDownlinkCapability={canConfigureDownlink}
                anchor={optionsRef.current}
            />
        ),
        [hideOptionsMenu, selectedSensor, onEditSensor, onDeleteSensor, onConfigureSensor, canConfigureDownlink]
    )

    const navigateBackWithFallback = () => {
        if (window.history.length > 1) {
            navigate(-1)
            return
        }

        navigate('/dashboard', { replace: true })
    }

    const doMobileBackClose = () => {
        if (isOnboardedEditMode) {
            setOnboardedEditMode(false)
            setEditSensorOpenedFromOptionsMenu(false)
        } else if (isDownlinkConfigMode) {
            setDownlinkConfigMode(false)
        } else {
            setSelectedSensorCard(null)
            navigateBackWithFallback()
        }

        setOnboardedEdited(false)
    }

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => {
            if (closeFromMobileBackRef.current) {
                doMobileBackClose()
            } else {
                doClose()
            }
            closeFromMobileBackRef.current = false
        },
        autoClose: true,
    })

    const handleEditOnboardedBackButton = () => {
        refetch()
        setOnboardedEditMode(false)
        setEditSensorOpenedFromOptionsMenu(false)
        if (!isEditSensorOpenedFromOptionsMenu) {
            setEditSensorOpenedFromOptionsMenu(false)
            doClose()
        }
    }

    const handleBackButton = () => {
        closeFromMobileBackRef.current = true
        if (onboardedEdited) {
            showConfirm()
        } else {
            doMobileBackClose()
        }
    }

    const handleSideBarCloseButton = () => {
        closeFromMobileBackRef.current = false
        if (onboardedEdited) {
            showConfirm()
        } else {
            doClose()
        }
    }

    const doClose = () => {
        if (isDownlinkConfigMode) {
            setDownlinkConfigMode(false)
            return
        }
        if (isEditSensorOpenedFromOptionsMenu) {
            setOnboardedEditMode(false)
            setEditSensorOpenedFromOptionsMenu(false)
        } else {
            setSelectedSensorCard(null)
            navigate('/dashboard')
        }
        setOnboardedEdited(false)
    }

    const handleDropDownButton = () => {
        setIsExpanded(!isExpanded)
    }

    const handleConfigurationsDropDownButton = () => {
        setIsConfigurationsExpanded(!isConfigurationsExpanded)
    }

    const displayImage = (image: ThumbnailDto) => {
        const index = files.findIndex((file) => file.id === image.id)
        setSelectedIndex(index)
        setImageOpen(true)
    }

    const isOnboardedStatus =
        selectedSensor?.status != null &&
        (selectedSensor.status === InstalledSensorStatus.Onboarded ||
            selectedSensor.status === InstalledSensorStatus.Active ||
            selectedSensor.status === InstalledSensorStatus.Offline ||
            selectedSensor.status === InstalledSensorStatus.Prepared)

    if (isOnboardedStatus && sensorAdditionalData.length === 0) {
        return <StyledSensorDetails>{LoadingIIoT('Fetching sensor details . . .')}</StyledSensorDetails>
    }

    const defaultData = '-'

    const formatDateForDisplay = (date: Date | null | undefined): string =>
        date
            ? new Date(date).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : defaultData

    const listEntries = [
        {
            label: 'Plant',
            data: [tagDetails?.instCode ?? defaultData, tagDetails?.plantIdDescription ?? defaultData],
        },
        {
            label: 'Superior tag',
            data: [tagDetails?.superiorTag ?? defaultData, tagDetails?.superiorTagDescription ?? defaultData],
        },
        {
            label: 'Project',
            data: [tagDetails?.projectCode ?? defaultData, tagDetails?.projectCodeDescription ?? defaultData],
        },
        {
            label: 'System',
            data: [tagDetails?.system ?? defaultData, tagDetails?.systemDescription ?? defaultData],
        },
        {
            label: 'Area',
            data: [tagDetails?.locationCode ?? defaultData, tagDetails?.locationCodeDescription ?? defaultData],
        },
        { label: 'Serial number', data: [selectedSensor?.vendorSerialNumber ?? defaultData] },
        {
            label: 'Registered',
            data: [formatDateForDisplay(selectedSensor?.installationDate)],
        },
        {
            label: 'First active',
            data: [formatDateForDisplay(selectedSensor?.firstActive)],
        },
        {
            label: 'Last message',
            data: [formatTimestampForDisplay(selectedSensor?.lastReceived, defaultData)],
        },
    ]

    const openTrending = (url: string = buildTrendingUrl()) => {
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const handleTrendingClick = (url: string = buildTrendingUrl()) => {
        if (userSettings?.consentTrendingApp) {
            openTrending(url)
        } else {
            setPendingTrendingUrl(url)
            setShowTrendingConsent(true)
        }
    }

    const handleMeasurementTrendingClick = (measurementName?: string | null) => {
        handleTrendingClick(buildTrendingUrl(measurementName))
    }

    const handleTrendingConsentConfirm = async (dontShowAgain: boolean) => {
        if (dontShowAgain && userSettings) {
            try {
                setIsSavingTrendingConsent(true)
                await updateUserSettings({ ...userSettings, consentTrendingApp: true })
                queryClient.invalidateQueries({ queryKey: [QueryKeys.UserSettings] })
            } catch (error) {
                console.error('Failed to save trending consent', error)
            } finally {
                setIsSavingTrendingConsent(false)
            }
        }
        setShowTrendingConsent(false)
        openTrending(pendingTrendingUrl)
    }

    const ThumbnailsList: React.FC<{ images: ThumbnailDto[] | null }> = ({ images }) => {
        if (!images || images.length === 0) return null

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-start' }}>
                {images.map((image, index) => (
                    <Thumbnail
                        key={index}
                        src={image.thumbnail || ''}
                        alt={`Thumbnail ${index}`}
                        onClick={() => displayImage(image)}
                    />
                ))}
            </div>
        )
    }

    return (
        <>
            {isMobile && (
                <ButtonMobileGroup>
                    <BackButton onClick={handleBackButton} variant="ghost">
                        <Icon size={24} name="chevron_left" />
                        Back
                    </BackButton>
                    {!isOnboardedEditMode && !isDownlinkConfigMode && (
                        <Button onClick={showOptionsMenu} variant="ghost">
                            Options
                            <Pointer ref={optionsRef}>
                                <Icon size={24} data={more_vertical}></Icon>
                            </Pointer>
                        </Button>
                    )}
                </ButtonMobileGroup>
            )}
            {isOnboardedEditMode ? (
                <StyledSensorDetails>
                    <Header>
                        <TruncatedTypography variant="h1">
                            Edit {selectedSensor?.tag || selectedSensor?.vendorSerialNumber}
                        </TruncatedTypography>
                        {!isMobile && (
                            <ButtonGroup $marginLeft="auto">
                                <OptionsButton onClick={handleSideBarCloseButton} variant="ghost_icon">
                                    <Icon size={24} name="close"></Icon>
                                </OptionsButton>
                            </ButtonGroup>
                        )}
                    </Header>
                    <EditOnboarded
                        sensor={selectedSensor}
                        setSensor={setSelectedSensor}
                        sensorAdditionalData={sensorAdditionalData !== null ? tagDetails : null}
                        handleEditOnboardedBackButton={handleEditOnboardedBackButton}
                        hideOptionsMenu={hideOptionsMenu}
                    />
                </StyledSensorDetails>
            ) : isDownlinkConfigMode && selectedSensor ? (
                <StyledSensorDetails>
                    <Header>
                        <TruncatedTypography variant="h1">Change configuration</TruncatedTypography>
                        {!isMobile && (
                            <ButtonGroup $marginLeft="auto">
                                <OptionsButton onClick={() => setDownlinkConfigMode(false)} variant="ghost_icon">
                                    <Icon size={24} name="close"></Icon>
                                </OptionsButton>
                            </ButtonGroup>
                        )}
                    </Header>
                    <DownlinkConfiguration
                        sensor={selectedSensor}
                        supportedTypes={downlinkCapability?.supportedTypes ?? []}
                        onBack={() => setDownlinkConfigMode(false)}
                    />
                </StyledSensorDetails>
            ) : (
                <StyledSensorDetails>
                    <Header>
                        <TruncatedTypography variant="h1">Sensor details</TruncatedTypography>
                        {!isMobile && (
                            <ButtonGroup $marginLeft="auto">
                                {!isOnboardedEditMode && (
                                    <Button onClick={showOptionsMenu} variant="ghost_icon">
                                        <Pointer ref={optionsRef}>
                                            <Icon size={24} data={more_vertical}></Icon>
                                        </Pointer>
                                    </Button>
                                )}
                                <OptionsButton onClick={handleSideBarCloseButton} variant="ghost_icon">
                                    <Icon size={24} name="close"></Icon>
                                </OptionsButton>
                            </ButtonGroup>
                        )}
                    </Header>
                    <Subheading variant="h3">{selectedSensor?.tag || selectedSensor?.vendorSerialNumber}</Subheading>
                    <IconGroup>
                        {selectedSensor?.status === InstalledSensorStatus.Draft && (
                            <StatusContainer>
                                <Icon
                                    data={assignment_important}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Draft] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        {selectedSensor?.status === InstalledSensorStatus.Unsuccessful && (
                            <StatusContainer>
                                <Icon
                                    data={sync_problem}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Unsuccessful] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        {selectedSensor?.status === InstalledSensorStatus.Deleted && (
                            <StatusContainer>
                                <Icon
                                    data={close_circle_outlined}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Deleted] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        {selectedSensor?.status === InstalledSensorStatus.Prepared && (
                            <StatusContainer>
                                <Icon
                                    data={check_circle_outlined}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Prepared] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        {selectedSensor?.status === InstalledSensorStatus.Offline && (
                            <StatusContainer>
                                <Icon
                                    data={close_circle_outlined}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Offline] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        {selectedSensor?.status === InstalledSensorStatus.Active && (
                            <StatusContainer>
                                <Icon
                                    data={check_circle_outlined}
                                    size={24}
                                    style={{ color: STATUS_COLORS[InstalledSensorStatus.Active] }}
                                />
                                <DisplayValue>{selectedSensor.status}</DisplayValue>
                            </StatusContainer>
                        )}
                        <StatusContainer>
                            <BatteryLevelIcon status={selectedSensor?.batteryStatus} size={24} />
                            <DisplayValue>
                                {formatBatteryDisplay(selectedSensor?.battery, selectedSensor?.batteryEstimated)}
                                {selectedSensor?.batteryStatus ? ` (${selectedSensor.batteryStatus})` : ''}
                            </DisplayValue>
                        </StatusContainer>
                        <SignalStatusContainer>
                            <SignalQualityIcon quality={selectedSensor?.signalQuality} size={24} />
                            <DisplayValue>{formatSignalQualityDisplay(selectedSensor?.signalQuality)}</DisplayValue>
                        </SignalStatusContainer>
                    </IconGroup>
                    {selectedSensor?.status === InstalledSensorStatus.Unsuccessful && (
                        <SectionGroup>
                            <Headline>STATUS MESSAGE</Headline>
                            <Text style={{ color: STATUS_COLORS[InstalledSensorStatus.Unsuccessful] }}>
                                {selectedSensor?.statusMessage}
                            </Text>
                        </SectionGroup>
                    )}
                    {selectedSensor?.status === InstalledSensorStatus.Deleted && (
                        <SectionGroup>
                            <Headline>STATUS MESSAGE</Headline>
                            <Text style={{ color: STATUS_COLORS[InstalledSensorStatus.Deleted] }}>
                                {selectedSensor?.statusMessage}
                            </Text>
                        </SectionGroup>
                    )}
                    <SectionGroup>
                        <Headline>DEVICE</Headline>
                        {sensorDevice ? (
                            <DeviceLink
                                as="a"
                                href={`/portfolio/${encodeURIComponent(sensorDevice.key as string)}`}
                                variant="ghost"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault()
                                    // Clear the selected sensor card before leaving. It is
                                    // persisted in sessionStorage and, while set on mobile,
                                    // Layout forces the page's scroll container to
                                    // overflow-y: hidden — which would lock scrolling on the
                                    // Portfolio page we navigate to.
                                    setSelectedSensorCard(null)
                                    navigate(`/portfolio/${encodeURIComponent(sensorDevice.key as string)}`)
                                }}
                            >
                                {sensorDevice.displayName}
                            </DeviceLink>
                        ) : (
                            <Text>{defaultData}</Text>
                        )}
                    </SectionGroup>
                    {isOnboardedStatus && sensorAdditionalData.length > 0 ? (
                        <>
                            <SectionGroup>
                                <Headline>ADDITIONAL DESCRIPTION</Headline>
                                <Text>{tagDetails.description}</Text>
                            </SectionGroup>
                            <SectionGroup>
                                <Headline>IMAGES</Headline>
                                {files.length > 0 ? (
                                    <>
                                        <ThumbnailsList images={paginatedData} />
                                        <PaginationContainer>
                                            <Pagination
                                                totalItems={files.length}
                                                itemsPerPage={itemsPerPage}
                                                defaultPage={currentPageIndex}
                                                onChange={(e, page) => setCurrentPageIndex(page)}
                                                withItemIndicator={false}
                                            />
                                        </PaginationContainer>
                                    </>
                                ) : (
                                    <div>-</div>
                                )}
                            </SectionGroup>

                            <List>
                                {listEntries.map((entry) => (
                                    <ListItem key={entry.label} $addUnderline={true}>
                                        <ListEntryLabel>{entry.label}</ListEntryLabel>
                                        <ListEntryData>
                                            {entry.data.map((data, index) =>
                                                index === 0 && entry.data.length > 1 ? (
                                                    <ListEntryDataInstance
                                                        key={`${entry.label}-${index}`}
                                                        $fontWeight="bold"
                                                    >
                                                        {data}
                                                    </ListEntryDataInstance>
                                                ) : (
                                                    <ListEntryDataInstance key={`${entry.label}-${index}`}>
                                                        {data}
                                                    </ListEntryDataInstance>
                                                )
                                            )}
                                        </ListEntryData>
                                    </ListItem>
                                ))}
                            </List>
                            <ButtonGroup $marginBottom="25px" $justifyContent="flex-start" $gap="24px">
                                <Button
                                    as="a"
                                    href={
                                        isDev
                                            ? `https://stidtest.equinor.com/${selectedSensor?.plant}/tag?tagNo=${selectedSensor?.tag}`
                                            : `https://stid.equinor.com/${selectedSensor?.plant}/tag?tagNo=${selectedSensor?.tag}`
                                    }
                                    target="_blank"
                                    variant="ghost"
                                >
                                    STID
                                    <Icon name="external_link" />
                                </Button>
                                <Button
                                    as="a"
                                    href={`https://echo.equinor.com/echo3d?instCode=${selectedSensor?.plant}&tagNo=${selectedSensor?.tag}`}
                                    target="_blank"
                                    variant="ghost"
                                >
                                    Echo
                                    <Icon name="external_link" />
                                </Button>
                                <Tooltip
                                    title={isDev ? 'Trending only works in production environment' : ''}
                                    placement="top"
                                >
                                    <Button
                                        variant="ghost"
                                        aria-disabled={isDev}
                                        onClick={isDev ? undefined : () => handleTrendingClick()}
                                    >
                                        Trending
                                        <Icon name="external_link" />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                            <MeasurementContainer $isExpanded={isExpanded}>
                                <MeasurementsHeader
                                    color="primary"
                                    $isExpanded={isExpanded}
                                    onClick={handleDropDownButton}
                                    role="button"
                                >
                                    <DropDownTitle $isExpanded={isExpanded} bold={isExpanded} color="primary">
                                        Measurements
                                    </DropDownTitle>
                                    <DropDownIcon
                                        $isExpanded={isExpanded}
                                        name={isExpanded ? 'chevron_up' : 'chevron_down'}
                                    />
                                </MeasurementsHeader>
                                {isExpanded && (
                                    <MeasurementsDataContainer $isExpanded={isExpanded}>
                                        {isLoadingMeasurements ? (
                                            <MeasurementsLoadingRow>
                                                <Progress.Dots color="primary" />
                                            </MeasurementsLoadingRow>
                                        ) : hasMeasurementsError ? (
                                            <MeasurementsMessageRow>
                                                <MeasurementsMessageIcon
                                                    data={warning_outlined}
                                                    size={16}
                                                    color="#8A4100"
                                                />
                                                <PendingConfigurationText>
                                                    {measurementsErrorText}
                                                </PendingConfigurationText>
                                            </MeasurementsMessageRow>
                                        ) : hasMeasurements ? (
                                            <MeasurementsList
                                                measurementData={measurementData}
                                                showTrendLinks={isAdmin}
                                                isTrendingDisabled={isDev}
                                                onTrendingClick={handleMeasurementTrendingClick}
                                            />
                                        ) : (
                                            <MeasurementPropertyValue>
                                                No measurements available for this sensor.
                                            </MeasurementPropertyValue>
                                        )}
                                    </MeasurementsDataContainer>
                                )}
                            </MeasurementContainer>
                            {configurations && (configurations.confirmed || configurations.pendingConfiguration) && (
                                <ConfigurationsContainer $isExpanded={isConfigurationsExpanded}>
                                    <MeasurementsHeader
                                        color="primary"
                                        $isExpanded={isConfigurationsExpanded}
                                        onClick={handleConfigurationsDropDownButton}
                                        role="button"
                                    >
                                        <DropDownTitle
                                            $isExpanded={isConfigurationsExpanded}
                                            bold={isConfigurationsExpanded}
                                            color="primary"
                                        >
                                            Configurations
                                        </DropDownTitle>
                                        <DropDownIcon
                                            $isExpanded={isConfigurationsExpanded}
                                            name={isConfigurationsExpanded ? 'chevron_up' : 'chevron_down'}
                                        />
                                    </MeasurementsHeader>
                                    {isConfigurationsExpanded && (
                                        <MeasurementsDataContainer $isExpanded={isConfigurationsExpanded}>
                                            {configurations.confirmed && (
                                                <MeasurementItemContainer
                                                    key={configurations.confirmed.sensorMetadataConfigurationDbKey}
                                                >
                                                    <ConfigurationNameRow>
                                                        <MeasurementName>
                                                            Temperature measurement interval
                                                        </MeasurementName>
                                                        <div ref={configurationHelpRef}>
                                                            <Button
                                                                variant="ghost_icon"
                                                                aria-label="Temperature measurement interval info"
                                                                onClick={() =>
                                                                    setConfigurationHelpOpen(!isConfigurationHelpOpen)
                                                                }
                                                            >
                                                                <Icon data={help_outline} size={24} />
                                                            </Button>
                                                        </div>
                                                    </ConfigurationNameRow>
                                                    <PopoverDialog
                                                        open={isConfigurationHelpOpen}
                                                        anchorEl={configurationHelpRef.current}
                                                        onClose={() => setConfigurationHelpOpen(false)}
                                                        title="Temperature measurement interval"
                                                        content={
                                                            'Number of seconds between each temperature measurement.\n\nChanging this value has an effect on responsiveness and battery life.'
                                                        }
                                                    />
                                                    <ConfigurationValue>
                                                        <MeasurementLatestValue>{`${configurations.confirmed.seconds} sec`}</MeasurementLatestValue>
                                                    </ConfigurationValue>
                                                </MeasurementItemContainer>
                                            )}
                                            {configurations.pendingConfiguration && (
                                                <MeasurementItemContainer
                                                    key={
                                                        configurations.pendingConfiguration
                                                            .sensorMetadataConfigurationDbKey
                                                    }
                                                >
                                                    <PendingConfigurationRow>
                                                        <PendingConfigurationIcon
                                                            data={info_circle}
                                                            size={16}
                                                            color="#8A4100"
                                                        />
                                                        <PendingConfigurationText>
                                                            Saved changes that will be applied within 24 hours:
                                                            <NormalColorText>
                                                                {configurations.pendingConfiguration.seconds} sec
                                                            </NormalColorText>
                                                        </PendingConfigurationText>
                                                    </PendingConfigurationRow>
                                                </MeasurementItemContainer>
                                            )}
                                        </MeasurementsDataContainer>
                                    )}
                                </ConfigurationsContainer>
                            )}
                        </>
                    ) : (
                        <>
                            <SectionGroup>
                                <Headline>ADDITIONAL DESCRIPTION</Headline>
                                <Text>
                                    {selectedSensor?.userAddedDescription ? selectedSensor.userAddedDescription : '-'}
                                </Text>
                            </SectionGroup>
                            <SectionGroup>
                                <Headline>IMAGES</Headline>
                                <Text>{sensorImages?.length ? sensorImages.length + ' images' : '-'}</Text>
                            </SectionGroup>
                            <List>
                                <ListItem $addUnderline={true}>
                                    <ListEntryLabel>Plant</ListEntryLabel>
                                    <ListEntryDataInstance>{selectedSensor?.plant}</ListEntryDataInstance>
                                </ListItem>
                                <ListItem $addUnderline={true}>
                                    <ListEntryLabel>Project</ListEntryLabel>
                                    <ListEntryDataInstance>{selectedSensor?.project}</ListEntryDataInstance>
                                </ListItem>
                                <ListItem $addUnderline={true}>
                                    <ListEntryLabel>Serial number</ListEntryLabel>
                                    <ListEntryDataInstance>{selectedSensor?.vendorSerialNumber}</ListEntryDataInstance>
                                </ListItem>
                                <ListItem $addUnderline={true}>
                                    <ListEntryLabel>Superior tag</ListEntryLabel>
                                    <ListEntryDataInstance>{selectedSensor?.superiorTag}</ListEntryDataInstance>
                                </ListItem>
                                <ListItem $addUnderline={true}>
                                    <ListEntryLabel>Area</ListEntryLabel>
                                    <ListEntryDataInstance>{selectedSensor?.area}</ListEntryDataInstance>
                                </ListItem>
                            </List>
                        </>
                    )}
                    {isOptionsMenuOpen && !isOnboardedEditMode && renderOptionsMenu()}
                    {imageOpen && (
                        <ImageDisplayer
                            plant={selectedSensor?.plant ? selectedSensor?.plant : ''}
                            showImage={imageOpen}
                            onClose={() => setImageOpen(false)}
                            files={files ?? []}
                            tag={selectedSensor?.tag ? selectedSensor?.tag : ''}
                            index={selectedIndex}
                        />
                    )}
                    {showTrendingConsent && (
                        <TrendingConsentDialog
                            isBusy={isSavingTrendingConsent}
                            onCancel={() => setShowTrendingConsent(false)}
                            onConfirm={handleTrendingConsentConfirm}
                        />
                    )}
                </StyledSensorDetails>
            )}
        </>
    )
}

export default SensorDetails

const Pointer = styled.div`
    cursor: pointer;
`

const BackButton = styled(Button)`
    display: flex;
    font-family: Equinor;
    font-size: 14px;
    color: #007079;
    align-items: center;
`

const OptionsButton = styled(Button)`
    margin-right: 0;
`

const StyledSensorDetails = styled.div<{
    $height?: string
}>`
    display: flex;
    flex-direction: column;
    position: fixed;
    inset: 64px 0 0 auto;
    width: 400px;
    min-width: 350px;
    background-color: #ffffff;
    box-shadow: -5px 0 5px rgba(0, 0, 0, 0.1);
    padding: 16px;
    z-index: 1000;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 1024px) {
        all: unset;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        padding: 16px;
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }
`

const Header = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    margin-bottom: 10px;
    align-items: center;
`

const TruncatedTypography = styled(Typography)`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
`

const Subheading = styled(Typography)<{
    $marginBottom?: string
    $marginTop?: string
    $fontWeight?: string
}>`
    margin-top: ${({ $marginTop }) => ($marginTop ? $marginTop : '0px')};
    margin-bottom: ${({ $marginBottom }) => ($marginBottom ? $marginBottom : '0px')};
    font-weight: ${({ $fontWeight }) => ($fontWeight ? $fontWeight : 'normal')};
`

const Headline = styled(Typography)`
    margin-top: 0;
    margin-bottom: 4px;
    font-family: Equinor;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 1.1px;
    text-transform: uppercase;
`

const Text = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
`

const DeviceLink = styled(Button)`
    padding-left: 0;
    padding-right: 0;
    height: auto;
    min-width: 0;
    justify-content: flex-start;
    text-decoration: none;
    color: #1f6369;

    &:hover {
        background: transparent;
        text-decoration: underline;
        color: #1f6369;
    }
`

const SectionGroup = styled.div`
    margin-bottom: 16px;
`

const ButtonGroup = styled.div<{
    $marginLeft?: string
    $marginTop?: string
    $marginBottom?: string
    $justifyContent?: string
    $gap?: string
}>`
    display: flex;
    margin-left: ${({ $marginLeft }) => ($marginLeft ? $marginLeft : '0px')};
    margin-top: ${({ $marginTop }) => ($marginTop ? $marginTop : '0px')};
    margin-bottom: ${({ $marginBottom }) => ($marginBottom ? $marginBottom : '0px')};
    justify-content: ${({ $justifyContent }) => ($justifyContent ? $justifyContent : 'space-between')};
    gap: ${({ $gap }) => ($gap ? $gap : '0px')};

    @media (max-width: 1024px) {
        margin-left: 0;
        gap: 1rem;
    }
`

const ButtonMobileGroup = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    flex-shrink: 0;
    padding-top: 16px;
    padding-bottom: 8px;
`

const IconGroup = styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: 16px;

    @media (max-width: 1024px) {
        margin-left: 0;
    }
`

const List = styled.ul`
    list-style: none;
    padding-left: 0;
    text-align: left;
`

const ListItem = styled.li<{
    $addUnderline?: boolean
    $marginBottom?: string
}>`
    display: flex;
    flex-direction: row;
    font-family: Equinor;
    font-size: 14px;
    line-height: 16px;
    align-items: center;

    margin-bottom: ${({ $marginBottom }) => ($marginBottom ? $marginBottom : '10px')};
    border-bottom: ${({ $addUnderline }) => ($addUnderline ? '1px solid rgba(0, 0, 0, 0.1)' : 'unset')};
`

const ListEntryLabel = styled(Typography)<{
    $fontWeight?: string
}>`
    display: flex;
    flex: 0 0 40%;
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    line-height: 25px;
    font-weight: ${({ $fontWeight }) => ($fontWeight ? $fontWeight : 'normal')};
    text-decoration: none;
`

const ListEntryData = styled.div`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    overflow: hidden;
`

const ListEntryDataInstance = styled(Typography)<{
    $fontWeight?: string
}>`
    word-wrap: break-word;
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    line-height: 25px;
    font-weight: ${({ $fontWeight }) => ($fontWeight ? $fontWeight : 'normal')};
    text-decoration: none;
`

const MeasurementItemContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const MeasurementHeaderRow = styled.div`
    display: grid;
    grid-template-columns: 40% minmax(0, 1fr);
    column-gap: 4px;
    align-items: baseline;
`

const MeasurementName = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: bold;
    line-height: 25px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const MeasurementLatestValue = styled(Typography)`
    word-wrap: break-word;
    min-width: 0;
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: normal;
    line-height: 25px;
    overflow: hidden;
`

const ConfigurationValue = styled.div`
    margin-top: 4px;
`

const ConfigurationNameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const MeasurementPropertyRow = styled.div<{ $marginTop?: string }>`
    display: grid;
    grid-template-columns: 40% minmax(0, 1fr);
    column-gap: 4px;
    margin-top: ${({ $marginTop }) => $marginTop || '4px'};
    align-items: baseline;
`

const MeasurementPropertyLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    line-height: 25px;
    font-weight: normal;
`

const MeasurementPropertyValue = styled(Typography)`
    word-wrap: break-word;
    min-width: 0;
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    line-height: 25px;
    font-weight: normal;
    overflow: hidden;
`

const MeasurementDivider = styled.div`
    margin-top: 24px;
    margin-bottom: 24px;
    margin-left: 1px;
    margin-right: 1px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`

const PendingConfigurationRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 4px;
    color: #8a4100;
    margin-top: 8px;
`

const PendingConfigurationIcon = styled(Icon)`
    flex-shrink: 0;
`

const MeasurementsMessageRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 4px;
    color: #8a4100;
`

const MeasurementsMessageIcon = styled(Icon)`
    flex-shrink: 0;
`

const MeasurementsLoadingRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 40px;
`

const NormalColorText = styled.span`
    display: block;
    color: #000000;
`

const PendingConfigurationText = styled(Typography)`
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.2px;
    color: inherit;
    flex: 1;
    min-width: 0;
    white-space: normal;
    overflow-wrap: break-word;
`

const StatusContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const SignalStatusContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const DisplayValue = styled(Typography)`
    margin-left: 5px;
`

const MeasurementContainer = styled(Card)<{ $isExpanded?: boolean }>`
    position: relative;
    display: block;
    width: 100%;
    height: ${({ $isExpanded }) => ($isExpanded ? 'auto' : '48px')};
    flex-shrink: 0;
    box-shadow: 0px 3px 4px 0px #0000001f;
    box-shadow: 0px 2px 4px 0px #00000024;
    margin-bottom: 16px;

    @media (max-width: 1024px) {
        height: ${({ $isExpanded }) => ($isExpanded ? 'auto' : '48px')};
    }
`

const ConfigurationsContainer = styled(MeasurementContainer)`
    margin-top: 8px;
`

const MeasurementsHeader = styled.div<{
    $isExpanded?: boolean
}>`
    display: flex;
    width: 100%;
    height: 48px;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background-color: ${({ $isExpanded }) => !$isExpanded && '#ddececff'};
    }
`

const DropDownTitle = styled(Typography)<{ $isExpanded?: boolean }>`
    margin-left: 15px;
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
`

const DropDownIcon = styled(Icon)<{ $isExpanded?: boolean }>`
    position: relative;
    margin-left: auto;
    right: 10px;
    size: 12;
    color: #007079;
`

const MeasurementsDataContainer = styled.div<{
    $isExpanded?: boolean
}>`
    display: flex;
    flex-direction: column;
    padding: 24px 15px 15px 15px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    height: ${({ $isExpanded }) => ($isExpanded ? '100%' : '0px')};
    overflow-y: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'hidden')};

    @media (max-width: 1024px) {
        max-height: none;
        margin-bottom: 15px;
        height: ${({ $isExpanded }) => ($isExpanded ? 'auto' : '48px')};
        overflow-y: visible;
    }
`

const Thumbnail = styled.img`
    width: 100px;
    height: 150px;

    border-radius: 5px;
    cursor: pointer;
`

const PaginationContainer = styled.div`
    display: flex;
    margin: 16px 0;
    justify-content: center;
`
